import { useState, useEffect } from "react";
import { PlusCircledIcon } from "@radix-ui/react-icons";
import { Column } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { DateRange } from "@revelate/types";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

interface DataTableDateRangeFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title?: string;
}

export function DataTableDateRangeFilter<TData, TValue>({
  column,
  title,
}: DataTableDateRangeFilterProps<TData, TValue>) {
  const dateRange = column?.getFilterValue() as DateRange | undefined;

  const handleDateRangeChange = (newRange: DateRange | undefined) => {
    column?.setFilterValue(newRange);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed">
          <PlusCircledIcon className="mr-2 h-4 w-4" />
          {title}
          {dateRange && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 font-normal"
              >
                {dateRange.from && format(dateRange.from, "yyyy-MM-dd")} 
                {dateRange.from && dateRange.to && " - "} 
                {dateRange.to && format(dateRange.to, "yyyy-MM-dd")} 
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[550px] p-0" align="start">
        <Calendar
          mode="range"
          numberOfMonths={2}
          selected={dateRange}
          onSelect={handleDateRangeChange}
        />
      </PopoverContent>
    </Popover>
  );
}
